import { useEffect, useRef } from 'react';

export interface ContextMenuItem {
  label: string;
  onClick?: () => void;
  separator?: boolean;
  danger?: boolean;
  disabled?: boolean;
  shortcut?: string;
  icon?: React.ReactNode;
}

interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

/**
 * Floating right-click context menu. Positions itself at the requested
 * (x, y) but clamps inside the viewport so it never opens off-screen.
 * Closes on outside-click, Escape, or any item activation.
 */
export function ContextMenu({ x, y, items, onClose }: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Clamp the menu inside the viewport once it's mounted.
    const el = ref.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let left = x;
      let top = y;
      if (left + rect.width + 8 > vw) left = vw - rect.width - 8;
      if (top + rect.height + 8 > vh) top = vh - rect.height - 8;
      el.style.left = `${Math.max(4, left)}px`;
      el.style.top = `${Math.max(4, top)}px`;
    }

    const onDocDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('mousedown', onDocDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDocDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [x, y, onClose]);

  return (
    <div
      ref={ref}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        position: 'fixed',
        left: x,
        top: y,
        minWidth: 220,
        background: 'var(--panel)',
        border: '1px solid var(--panel-border)',
        borderRadius: 'var(--radius-sm)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
        padding: '4px 0',
        zIndex: 10000,
        fontSize: 12,
        color: 'var(--text)',
      }}
    >
      {items.map((item, i) => {
        if (item.separator) {
          return (
            <div
              key={i}
              style={{
                height: 1,
                background: 'var(--panel-border)',
                margin: '4px 0',
              }}
            />
          );
        }
        return (
          <button
            key={i}
            disabled={item.disabled}
            onClick={() => {
              if (item.disabled) return;
              item.onClick?.();
              onClose();
            }}
            style={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              gap: 10,
              padding: '6px 14px',
              background: 'transparent',
              border: 'none',
              color: item.danger ? 'var(--danger)' : 'var(--text)',
              opacity: item.disabled ? 0.4 : 1,
              cursor: item.disabled ? 'default' : 'pointer',
              fontSize: 12,
              textAlign: 'left',
            }}
            onMouseEnter={(e) => {
              if (!item.disabled)
                e.currentTarget.style.background = 'var(--accent-soft)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            {item.icon && (
              <span
                style={{
                  display: 'inline-flex',
                  width: 14,
                  justifyContent: 'center',
                  color: 'var(--text-muted)',
                }}
              >
                {item.icon}
              </span>
            )}
            <span style={{ flex: 1 }}>{item.label}</span>
            {item.shortcut && (
              <span
                style={{
                  fontSize: 10,
                  color: 'var(--text-dim)',
                  fontFamily: 'ui-monospace, Consolas, monospace',
                }}
              >
                {item.shortcut}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
